'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

export default function SecurityLock() {
  const pathname = usePathname();
  
  // 與後端邏輯一致：本地環境始終開鎖
  const isLocked = typeof window !== 'undefined' && 
    window.location.hostname !== 'localhost' && 
    process.env.NEXT_PUBLIC_SECURITY_LOCK === 'true';
  
  // 🔍 除錯標記：組件載入（暫時啟用以診斷問題）
  const debugLog = (msg: string, ...args: any[]) => {
    if (process.env.NEXT_PUBLIC_SECURITY_LOCK_DEBUG === 'true') {
      // 僅在啟用除錯時輸出
      console.log(msg, ...args);
    }
  };
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    debugLog('🔒 [SecurityLock] 組件已載入');
    debugLog('🔒 [SecurityLock] 當前路徑:', pathname);
    debugLog('🔒 [SecurityLock] Hostname:', window.location.hostname);
    debugLog('🔒 [SecurityLock] 環境變數:', process.env.NEXT_PUBLIC_SECURITY_LOCK);
    debugLog('🔒 [SecurityLock] 鎖定狀態:', isLocked);
  }
  
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      debugLog('🔒 [SecurityLock] useEffect 開始執行');
      debugLog('🔒 [SecurityLock] isLocked:', isLocked);
    }
    
    if (!isLocked) {
      if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
        debugLog('🔒 [SecurityLock] 未鎖定，跳過保護機制');
        debugLog('🔒 [SecurityLock] 原因: hostname !== localhost:', window.location.hostname !== 'localhost');
        debugLog('🔒 [SecurityLock] 原因: NEXT_PUBLIC_SECURITY_LOCK === "true":', process.env.NEXT_PUBLIC_SECURITY_LOCK === 'true');
      }
      return;
    }
    
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      debugLog('🔒 [SecurityLock] 開始啟用保護機制');
    }
    
    // Skip for admin
    if (typeof window !== 'undefined' && localStorage.getItem('admin') === 'true') {
      // console.log('🔒 [SecurityLock] 管理員模式，跳過保護機制');
      return;
    }
    
    // Skip for admin routes
    if (pathname?.startsWith('/admin')) {
      // console.log('🔒 [SecurityLock] 管理員頁面，跳過保護機制');
      return;
    }
    
    // console.log('🔒 [SecurityLock] 開始綁定事件監聽器');
    
    // Disable keyboard shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // F12
      if (e.keyCode === 123 || e.key === 'F12') {
        // console.log('🔒 [SecurityLock] F12 被阻擋');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
      
      // Ctrl+Shift+I, Ctrl+Shift+J (Developer Tools)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.keyCode === 73 || e.keyCode === 74 || e.key === 'I' || e.key === 'J')) {
        // console.log('🔒 [SecurityLock] Ctrl+Shift+I/J 被阻擋');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
      
      // Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.keyCode === 85 || e.key === 'u' || e.key === 'U')) {
        // console.log('🔒 [SecurityLock] Ctrl+U 被阻擋');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
      
      // Ctrl+S (Save)
      if ((e.ctrlKey || e.metaKey) && (e.keyCode === 83 || e.key === 's' || e.key === 'S')) {
        // console.log('🔒 [SecurityLock] Ctrl+S 被阻擋');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };
    
    // Disable right click
    const handleContextMenu = (e: MouseEvent) => {
      // console.log('🔒 [SecurityLock] 右鍵被阻擋');
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    };
    
    // Disable text selection
    const handleSelectStart = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && target.tagName !== 'SELECT') {
        // console.log('🔒 [SecurityLock] 文字選擇被阻擋');
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }
    };
    
    // Disable drag
    const handleDragStart = (e: DragEvent) => {
      // console.log('🔒 [SecurityLock] 拖拽被阻擋');
      e.preventDefault();
      return false;
    };
    
    // Add event listeners with capture phase (更高優先級，確保優先執行)
    // console.log('🔒 [SecurityLock] 綁定事件監聽器完成');
    document.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    document.addEventListener('contextmenu', handleContextMenu, { capture: true, passive: false });
    document.addEventListener('selectstart', handleSelectStart, { capture: true, passive: false });
    document.addEventListener('dragstart', handleDragStart, { capture: true, passive: false });
    
    // 同時綁定到 window 以確保覆蓋所有情況
    window.addEventListener('keydown', handleKeyDown, { capture: true, passive: false });
    window.addEventListener('contextmenu', handleContextMenu, { capture: true, passive: false });
    window.addEventListener('selectstart', handleSelectStart, { capture: true, passive: false });
    window.addEventListener('dragstart', handleDragStart, { capture: true, passive: false });
    
    // DevTools detection（加強版：多種偵測方法）
    // console.log('🔒 [SecurityLock] 開始開發者工具偵測');
    const devtools = { open: false, lastCheck: Date.now() };
    let checkInterval: NodeJS.Timeout;
    
    // 方法 1: 尺寸差異檢測（最可靠的方法）
    const checkSizeDifference = () => {
      const heightDiff = window.outerHeight - window.innerHeight;
      const widthDiff = window.outerWidth - window.innerWidth;
      // 降低 threshold 以提高靈敏度（從 160 降到 50）
      // 開發者工具在底部或側邊時，即使差異較小也能偵測到
      return heightDiff > 50 || widthDiff > 50;
    };
    
    // 方法 2: 檢查 window 和 screen 的差異（開發者工具開啟時會有差異）
    const checkScreenDifference = () => {
      try {
        // screen.height 和 window.outerHeight 的差異
        const screenHeightDiff = screen.height - window.outerHeight;
        const screenWidthDiff = screen.width - window.outerWidth;
        // 如果差異很大，可能是開發者工具開啟了
        return screenHeightDiff > 200 || screenWidthDiff > 200;
      } catch (e) {
        return false;
      }
    };
    
    // 方法 3: 檢查 devtools 對象（如果存在）
    const checkDevToolsObject = () => {
      try {
        // Chrome DevTools 會暴露一些全局對象
        return !!(window as any).devtools || 
               !!(window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__ ||
               !!(window as any).__REDUX_DEVTOOLS_EXTENSION__ ||
               !!(window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__;
      } catch (e) {
        return false;
      }
    };
    
    // 方法 4: 檢查 console 對象的性能（開發者工具開啟時 console 操作會變慢）
    const checkConsolePerformance = () => {
      try {
        const start = performance.now();
        // 執行一個簡單的 console 操作
        const dummy = console.log.toString();
        const end = performance.now();
        // 如果執行時間 > 5ms，可能是開發者工具開啟了
        return end - start > 5;
      } catch (e) {
        return false;
      }
    };
    
    const checkDevTools = () => {
      const sizeDiff = checkSizeDifference();
      const screenDiff = checkScreenDifference();
      const devtoolsObject = checkDevToolsObject();
      const consolePerf = checkConsolePerformance();
      
      // 如果任一方法偵測到開發者工具
      if (sizeDiff || screenDiff || devtoolsObject || consolePerf) {
        if (!devtools.open) {
          devtools.open = true;
          devtools.lastCheck = Date.now();
          // console.log('🔒 [SecurityLock] 偵測到開發者工具開啟！');
          
          // 立即清除 console（如果用戶沒有覆蓋）
          try {
            if (console.clear && typeof console.clear === 'function') {
              console.clear();
            }
          } catch (e) {
            // 忽略清除失敗（用戶可能已覆蓋）
          }
          
          // 預設不打擾使用者；僅在明確啟用時才提示與干預
          if (process.env.NEXT_PUBLIC_ENABLE_DEVTOOLS_GUARD === 'true') {
            document.body.style.display = 'none';
            alert('請勿使用開發者工具');
            window.location.href = '/';
          }
        }
      } else {
        // 如果偵測到開發者工具已關閉
        if (devtools.open) {
          devtools.open = false;
          document.body.style.display = '';
        }
      }
    };
    
    checkInterval = setInterval(checkDevTools, 500); // 提高檢查頻率到 500ms
    // console.log('🔒 [SecurityLock] 開發者工具偵測定時器已啟動 (500ms)');
    
    // Clear console
    // console.log('🔒 [SecurityLock] 開始控制台清理');
    const clearConsole = () => {
      if (console.clear) {
        console.clear();
      }
    };
    
    const consoleInterval = setInterval(clearConsole, 1000);
    // console.log('🔒 [SecurityLock] 控制台清理定時器已啟動 (1000ms)');
    
    // Cleanup
    return () => {
      // console.log('🔒 [SecurityLock] 開始清理事件監聽器和定時器');
      document.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
      document.removeEventListener('contextmenu', handleContextMenu, { capture: true } as any);
      document.removeEventListener('selectstart', handleSelectStart, { capture: true } as any);
      document.removeEventListener('dragstart', handleDragStart, { capture: true } as any);
      window.removeEventListener('keydown', handleKeyDown, { capture: true } as any);
      window.removeEventListener('contextmenu', handleContextMenu, { capture: true } as any);
      window.removeEventListener('selectstart', handleSelectStart, { capture: true } as any);
      window.removeEventListener('dragstart', handleDragStart, { capture: true } as any);
      clearInterval(checkInterval);
      clearInterval(consoleInterval);
      // console.log('🔒 [SecurityLock] 清理完成');
    };
  }, [isLocked, pathname]);
  
  return null;
}
